import type { PlaceLayer } from '../types/Place';
import { LAYER_CONFIGS } from '../constants/layerConfig';

export type LayerOption = 'all' | PlaceLayer;

interface LayerSelectorProps {
  activeLayer: LayerOption;
  onLayerChange: (layer: LayerOption) => void;
  disabled?: boolean;
}

interface LayerSelectorOption {
  value: LayerOption;
  label: string;
  shortLabel: string;
  colorClass?: string;
}

// Build layer options from centralized config, adding "All" option at the start
const LAYERS: LayerSelectorOption[] = [
  { value: 'all', label: 'All', shortLabel: 'All' },
  ...LAYER_CONFIGS.map(config => ({
    value: config.id as LayerOption,
    label: config.label,
    shortLabel: config.shortLabel,
    colorClass: config.bgClass,
  })),
];

export function LayerSelector({
  activeLayer,
  onLayerChange,
  disabled = false,
}: LayerSelectorProps) {
  const handleKeyDown = (e: React.KeyboardEvent, layer: LayerOption) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onLayerChange(layer);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const currentIndex = LAYERS.findIndex(l => l.value === activeLayer);
      const nextIndex = (currentIndex + 1) % LAYERS.length;
      onLayerChange(LAYERS[nextIndex].value);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = LAYERS.findIndex(l => l.value === activeLayer);
      const prevIndex = (currentIndex - 1 + LAYERS.length) % LAYERS.length;
      onLayerChange(LAYERS[prevIndex].value);
    }
  };

  return (
    <div
      role="group"
      aria-label="Filter by category"
      className="flex items-center gap-1 p-1 bg-bg-surface/50 rounded-lg"
      data-testid="layer-selector"
    >
      {LAYERS.map((layer) => {
        const isActive = activeLayer === layer.value;
        return (
          <button
            key={layer.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={`Show ${layer.label}`}
            title={layer.label}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={() => !disabled && onLayerChange(layer.value)}
            onKeyDown={(e) => handleKeyDown(e, layer.value)}
            className={`
              min-h-[44px] px-2 lg:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md
              transition-colors duration-instant
              focus:outline-none focus:ring-2 focus:ring-primary/50
              ${isActive
                ? 'bg-primary text-white shadow-sm'
                : 'text-text-secondary hover:text-white hover:bg-white/10'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            data-testid={`layer-option-${layer.value}`}
          >
            {/* Short label with color dot on mobile, full label on desktop */}
            <span className="lg:hidden flex items-center gap-1.5">
              {layer.colorClass && (
                <span
                  className={`w-2 h-2 rounded-full shrink-0 ${layer.colorClass}`}
                  aria-hidden="true"
                />
              )}
              {layer.shortLabel}
            </span>
            <span className="hidden lg:inline">{layer.label}</span>
          </button>
        );
      })}
    </div>
  );
}
